import React, { useState, useEffect, useRef } from "react";
import { socket } from "../../../socket";
import { useAuth } from "context/AuthContext";
import { fetchChatHistory, uploadingFile } from "services/chat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip, faPaperPlane, faDownload } from "@fortawesome/free-solid-svg-icons";
import { extractTime } from "utils/extractTime";

const Chat = ({ chatId, usersNames }) => {
  const { userInfo } = useAuth();
  const fileInputRef = useRef(null);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const chatRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  };

  // Load history
  const loadHistory = async () => {
    if (!chatId) return; // <-- safe guard
    const res = await fetchChatHistory(chatId);
    setMessages(res.messages);
    setTimeout(scrollToBottom, 200);
  };

  // Socket
  useEffect(() => {
    if (!chatId) return;

    socket.connect();

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("join_room", chatId);
    });

    socket.on("receive_message", (msg) => {
      setMessages((prev) => [...prev, msg]);
      setTimeout(scrollToBottom, 100);
    });

    loadHistory();

    return () => {
      socket.off("receive_message");
      socket.disconnect();
      setConnected(false);
    };
  }, [chatId]);

  // Send message
  const sendMessage = (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    socket.emit("send_message", {
      room_id: chatId,
      sender_id: userInfo?.id,
      message_text: message,
      file_url: null,
      file_type: null,
    });

    setMessage("");
  };

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    sendFile(file);
  };

  const sendFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("room_id", chatId);
    formData.append("sender_id", userInfo?.id);

    await uploadingFile(formData, chatId);
  };

  if (!chatId) {
    return (
      <div className="w-[30%] border-black border-2 p-2 rounded-xl flex justify-center items-center text-white bg-gray-700">
        تحميل المحادثة...
      </div>
    );
  }

  return (
    <div className="md:w-[30%] w-full border-black border-2 p-2 rounded-xl flex flex-col gap-2 items-center">

      <div className="flex bg-gradient-to-b from-blue-900 to-blue-950 text-white rounded p-2 w-full">
        <p className="flex-1 text-center">
          محادثة {connected ? "🟢" : "🔴"}
        </p>

        <p className="text-end">
          <FontAwesomeIcon icon={faDownload} />
        </p>
      </div>

      {/* Messages container */}
      <div
        ref={chatRef}
        className="w-full max-h-60 h-60 bg-gradient-to-b from-blue-400 to-blue-600 rounded-xl flex flex-col p-4 overflow-y-auto space-y-3 shadow-inner"
      >
        {messages.map((m) => {
          const isMine = m.sender_id === userInfo?.id;
          if (m.file_url) {
            return (
              <a
                key={m.id}
                href={m.file_url}
                target="_blank"
                rel="noreferrer"
                className={`flex items-center gap-2 rounded-2xl py-2 px-4 max-w-[70%] shadow-md transition-all duration-200 border border-gray-200 
                  ${isMine ? "bg-white self-end text-gray-900" : "bg-blue-800 text-white"}`}
              >
                {m.file_name || "File Download"} <FontAwesomeIcon icon={faDownload} />
              </a>
            );
          }

          return (
            <div>
              <div className={`text-white text-sm font-bold ${isMine ? "text-end" : "text-start"}`}>{m?.sender_id === userInfo?.id ? "Me" : usersNames?.find(user => user.id === m?.sender_id)?.name || "stranger"}</div>
              <div key={m.id} className={`flex items-center gap-1 ${isMine ? "justify-end" : "justify-start"}`}>
                {!isMine && <div className="text-white text-xs">{extractTime(m.createdAt)}</div>}
                <div
                  className={`rounded-2xl py-2 px-4 max-w-[70%] shadow-md break-words ${isMine
                    ? "bg-white self-end text-gray-900 border border-gray-200"
                    : "bg-blue-800 text-white"
                    }`}
                >
                  {m.message_text}
                </div>
                {isMine && <div className="text-white text-xs">{extractTime(m.createdAt)}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Input area */}
      <form onSubmit={sendMessage} className="w-full flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={handleFileClick}
          className="text-blue-900 hover:text-blue-600 transition-all duration-200"
        >
          <FontAwesomeIcon icon={faPaperclip} size="lg" />
        </button>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />

      <input
        type="text"
        className="border-slate-400 border-2 rounded-full w-full px-4 py-2 text-end focus:outline-none focus:ring-2 focus:ring-blue-700"
        placeholder="ابدأ المحادثة"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />

      <button type="submit" className="text-blue-900 hover:text-blue-600 transition-all duration-200">
        <FontAwesomeIcon icon={faPaperPlane} size="lg" />
      </button>
    </form>
    </div >
  );
};

export default Chat;